import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Loader2, Building2, Users, CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Create Business, 2: Generate ICP
  const [loading, setLoading] = useState(false);
  const [businessId, setBusinessId] = useState(null);
  
  // Business form state
  const [businessData, setBusinessData] = useState({
    business_name: '',
    business_description: '',
    product_service: '',
    price: '',
    target_audience: '',
    industry: '',
    website: ''
  });

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    
    if (!businessData.business_description || !businessData.product_service || !businessData.price || !businessData.target_audience) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    try {
      setLoading(true);
      
      // Generate ICP (which creates business automatically)
      const response = await apiClient.post('/icp/generate', businessData);
      
      setBusinessId(response.data.business_id);
      setStep(2);
      toast.success('¡Empresa creada e ICP generado! 🎉');
      
      // Complete onboarding
      await apiClient.post('/user/complete-onboarding', null, {
        params: { business_id: response.data.business_id }
      });
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
      
    } catch (error) {
      console.error('Error in onboarding:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Límite de empresas alcanzado');
      } else {
        toast.error('Error al crear empresa. Por favor intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Bienvenido a Mónica
          </h1>
          <p className="text-xl text-gray-600">
            Tu Estratega de Marketing con IA
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
            {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
            <span className="font-semibold">1. Crear Empresa</span>
          </div>
          <div className="w-12 h-1 bg-gray-300"></div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
            {step > 2 ? <CheckCircle2 className="w-5 h-5" /> : <Users className="w-5 h-5" />}
            <span className="font-semibold">2. Generar ICP</span>
          </div>
        </div>

        {/* Step 1: Create Business */}
        {step === 1 && (
          <Card className="shadow-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Building2 className="w-6 h-6 text-purple-600" />
                Paso 1: Crea Tu Empresa
              </CardTitle>
              <CardDescription className="text-base">
                Cuéntanos sobre tu negocio para personalizar toda la experiencia
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateBusiness} className="space-y-6">
                <div>
                  <Label htmlFor="business_name">Nombre de la Empresa *</Label>
                  <Input
                    id="business_name"
                    value={businessData.business_name}
                    onChange={(e) => setBusinessData({...businessData, business_name: e.target.value})}
                    placeholder="Ej: Marketing Pro Agency"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">Nombre corto para identificar tu empresa</p>
                </div>

                <div>
                  <Label htmlFor="business_description">Descripción del Negocio *</Label>
                  <Textarea
                    id="business_description"
                    value={businessData.business_description}
                    onChange={(e) => setBusinessData({...businessData, business_description: e.target.value})}
                    placeholder="Ej: Agencia de marketing digital especializada en startups, ayudamos a empresas tech a escalar su presencia online..."
                    required
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="product_service">Producto/Servicio *</Label>
                  <Input
                    id="product_service"
                    value={businessData.product_service}
                    onChange={(e) => setBusinessData({...businessData, product_service: e.target.value})}
                    placeholder="Ej: Gestión de redes sociales, campañas de ads, branding"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Precio *</Label>
                    <Input
                      id="price"
                      value={businessData.price}
                      onChange={(e) => setBusinessData({...businessData, price: e.target.value})}
                      placeholder="Ej: $500/mes"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="industry">Industria</Label>
                    <Input
                      id="industry"
                      value={businessData.industry}
                      onChange={(e) => setBusinessData({...businessData, industry: e.target.value})}
                      placeholder="Ej: Marketing, SaaS, E-commerce"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="target_audience">Audiencia Objetivo *</Label>
                  <Input
                    id="target_audience"
                    value={businessData.target_audience}
                    onChange={(e) => setBusinessData({...businessData, target_audience: e.target.value})}
                    placeholder="Ej: Emprendedores y fundadores de startups tech"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="website">Sitio Web (Opcional)</Label>
                  <Input
                    id="website"
                    value={businessData.website}
                    onChange={(e) => setBusinessData({...businessData, website: e.target.value})}
                    placeholder="https://tuempresa.com"
                    type="url"
                  />
                </div>

                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-900 font-semibold mb-2">📌 Plan Gratuito:</p>
                  <p className="text-sm text-purple-800">
                    Puedes crear hasta 10 empresas gratis. Para crear más empresas, actualiza a Pro.
                  </p>
                </div>

                <Button type="submit" className="w-full h-14 text-lg" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Creando empresa y generando ICP (20-30 segundos)...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 w-5 h-5" />
                      Crear Empresa y Generar ICP
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: ICP Generated (Success State) */}
        {step === 2 && (
          <Card className="shadow-2xl bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="pt-12 pb-12 text-center">
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-16 h-16 text-white" />
                </div>
              </div>
              <h2 className="text-3xl font-bold mb-4 text-green-900">
                ¡Todo Listo! 🎉
              </h2>
              <p className="text-xl text-green-800 mb-6">
                Tu empresa ha sido creada y tu ICP generado exitosamente
              </p>
              <p className="text-green-700">
                Redirigiendo al Dashboard...
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-gray-600 text-sm">
          <p>Con Mónica, todo el contenido y estrategia será personalizado para tu empresa 🚀</p>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
